import { useBusiness } from '@/contexts/BusinessContext'
import { useAuth } from '@/contexts/AuthContext'
import { useLayout } from '@/contexts/LayoutContext'
import { CaretDown, SignOut, List } from '@phosphor-icons/react'
import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { MailboxHealthDot } from '@/components/shared/MailboxHealthDot'
import type { Business } from '@/types/database'

interface TopBarProps {
  onMobileMenuToggle: () => void
}

const dropdownVariants = {
  hidden: { opacity: 0, y: -8, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 400, damping: 30 },
  },
  exit: {
    opacity: 0,
    y: -4,
    scale: 0.98,
    transition: { duration: 0.15 },
  },
}

export function TopBar({ onMobileMenuToggle }: TopBarProps) {
  const { currentBusiness, businesses, setCurrentBusiness } = useBusiness()
  const { user, signOut } = useAuth()
  const { sidebarHidden } = useLayout()
  const navigate = useNavigate()
  const [businessOpen, setBusinessOpen] = useState(false)
  const [userOpen, setUserOpen] = useState(false)
  const businessRef = useRef<HTMLDivElement>(null)
  const userRef = useRef<HTMLDivElement>(null)

  // Picking a business in the switcher drives the URL: scoped inbox for a
  // specific business, or `/inbox` for the aggregate "All Businesses" view.
  // InboxPage's useEffect syncs `currentBusiness` back from the URL, so we
  // set context here for immediate feedback and avoid a flash.
  const handleSelectBusiness = (business: Business | null) => {
    setCurrentBusiness(business)
    setBusinessOpen(false)
    navigate(business ? `/inbox/${business.id}` : '/inbox')
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (businessRef.current && !businessRef.current.contains(e.target as Node)) {
        setBusinessOpen(false)
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserOpen(false)
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setBusinessOpen(false)
        setUserOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const initials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : '??'

  return (
    <header
      className={cn(
        'fixed top-4 right-4 h-14 bg-white/70 backdrop-blur-xl border border-border/50 rounded-[1.25rem] shadow-sm flex items-center justify-between px-5 z-40',
        sidebarHidden ? 'left-4' : 'left-4 md:left-[252px]',
      )}
    >
      {/* Hamburger — mobile by default, always visible when sidebar is hidden */}
      <button
        onClick={onMobileMenuToggle}
        aria-label="Open navigation menu"
        className={cn(
          'p-1.5 -ml-1 rounded-lg hover:bg-black/[0.04] text-text-muted transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none',
          !sidebarHidden && 'md:hidden',
        )}
      >
        <List size={20} weight="light" />
      </button>

      {/* Business Switcher */}
      <div ref={businessRef} className="relative">
        <button
          onClick={() => setBusinessOpen(!businessOpen)}
          aria-expanded={businessOpen}
          aria-haspopup="listbox"
          aria-label="Switch business"
          className="flex items-center gap-2 px-2.5 py-1.5 -ml-2.5 rounded-lg text-[15px] hover:bg-black/[0.04] transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none"
        >
          {currentBusiness?.color && (
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: currentBusiness.color }}
            />
          )}
          <span className="text-text-primary font-semibold text-[15px]">
            {currentBusiness?.name ?? 'All Businesses'}
          </span>
          <MailboxHealthDot
            businessId={currentBusiness?.id ?? null}
            size={14}
          />
          <CaretDown
            size={12}
            weight="light"
            className={cn(
              'text-text-muted transition-transform',
              businessOpen && 'rotate-180'
            )}
          />
        </button>

        <AnimatePresence>
          {businessOpen && (
            <motion.div
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute top-full left-0 mt-2 w-56 bg-white border border-border/50 rounded-[1.25rem] shadow-[0_16px_48px_rgba(0,0,0,0.12)] py-1.5 z-50"
            >
              {businesses.length > 1 && (
                <>
                  <button
                    onClick={() => handleSelectBusiness(null)}
                    className={cn(
                      'w-full text-left px-4 py-2.5 text-[15px] hover:bg-black/[0.03] transition-colors flex items-center gap-2',
                      !currentBusiness ? 'text-accent font-medium' : 'text-text-secondary'
                    )}
                  >
                    <span className="flex-1 min-w-0">All Businesses</span>
                    <MailboxHealthDot businessId={null} size={14} />
                  </button>
                  <div className="border-t border-border/50 mx-3 my-1" />
                </>
              )}
              {businesses.map((b) => (
                <button
                  key={b.id}
                  onClick={() => handleSelectBusiness(b)}
                  className={cn(
                    'w-full text-left px-4 py-2.5 text-[15px] hover:bg-black/[0.03] transition-colors flex items-center gap-2',
                    currentBusiness?.id === b.id ? 'text-accent font-medium' : 'text-text-primary'
                  )}
                >
                  {b.color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: b.color }}
                    />
                  )}
                  <span className="flex-1 min-w-0 truncate">{b.name}</span>
                  <MailboxHealthDot businessId={b.id} size={14} />
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* User Menu */}
      <div ref={userRef} className="relative">
        <button
          onClick={() => setUserOpen(!userOpen)}
          aria-expanded={userOpen}
          aria-haspopup="menu"
          aria-label="User menu"
          className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-black/[0.04] transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none"
        >
          <div className="w-8 h-8 rounded-full bg-accent/8 flex items-center justify-center">
            <span className="text-[10px] font-medium text-accent">{initials}</span>
          </div>
          <span className="text-text-secondary text-sm hidden sm:block">{user?.email}</span>
        </button>

        <AnimatePresence>
          {userOpen && (
            <motion.div
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute top-full right-0 mt-2 w-48 bg-white border border-border/50 rounded-[1.25rem] shadow-[0_16px_48px_rgba(0,0,0,0.12)] py-1.5 z-50"
            >
              <div className="px-4 py-2.5 border-b border-border/50">
                <p className="text-sm text-text-secondary truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => { signOut(); setUserOpen(false) }}
                className="w-full text-left px-4 py-2.5 text-[15px] hover:bg-black/[0.03] text-text-secondary flex items-center gap-2 transition-colors"
              >
                <SignOut size={16} weight="light" />
                Sign out
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  )
}
